import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Grid2,
  Paper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  Bolt,
  DevicesOther,
  Link as LinkIcon,
  Lightbulb,
  PowerSettingsNew,
  Search,
} from '@mui/icons-material';
import ButtonComponent from '../../../components/ButtonComponent';
import ModalComponent from '../../../components/ModalComponent';
import TextFieldComponent from '../../../components/TextFieldComponent';
import DropdownSearchbleComponent from '../../../components/DropdownSearchbleComponent';
import { AppContext } from '../../../context/AppContextProvider';
import {
  useGetDiscoveredHomeAssistantEntitiesAsync,
  useGetAllHomeAssistantDevicesByLicenseAsync,
  useLinkHomeAssistantDeviceAsync,
  useTriggerHomeAssistantDeviceAsync,
} from '../../../services/Devices/HomeAssistantDeviceService/hooks/useMutation';
import {
  IHomeAssistantDeviceProps,
  IHomeAssistantDiscoveredEntity,
} from '../../../services/Devices/HomeAssistantDeviceService/types';
import { useGetAllDeviceGroupByLicenseAsync } from '../../../services/DeviceGroupService/hooks/useMutation';
import { IDeviceGroupSimpleProps } from '../../../services/DeviceGroupService/types';
import StatTile from './StatTile';

const cardSx = () => ({
  borderRadius: 4,
  p: 2.5,
  border: `1px solid ${alpha('#ffffff', 0.1)}`,
  backgroundColor: alpha('#ffffff', 0.05),
});

const DevicesTab = () => {
  const { showSnackbar } = useContext(AppContext);

  const [discovered, setDiscovered] = useState<IHomeAssistantDiscoveredEntity[]>([]);
  const [devices, setDevices] = useState<IHomeAssistantDeviceProps[]>([]);
  const [groups, setGroups] = useState<IDeviceGroupSimpleProps[]>([]);
  const [entityToLink, setEntityToLink] = useState<IHomeAssistantDiscoveredEntity>();
  const [linkName, setLinkName] = useState('');
  const [linkGroupId, setLinkGroupId] = useState<number>();
  const [triggeringId, setTriggeringId] = useState<number>();

  const { mutateAsync: mutateGetDiscovered, isLoading: isLoadingDiscovered } =
    useGetDiscoveredHomeAssistantEntitiesAsync();
  const { mutateAsync: mutateGetDevices, isLoading: isLoadingDevices } =
    useGetAllHomeAssistantDevicesByLicenseAsync();
  const { mutateAsync: mutateGetGroups } = useGetAllDeviceGroupByLicenseAsync();
  const { mutateAsync: mutateLink, isLoading: isLoadingLink } =
    useLinkHomeAssistantDeviceAsync();
  const { mutateAsync: mutateTrigger } = useTriggerHomeAssistantDeviceAsync();

  const reload = () => {
    mutateGetDiscovered().then(setDiscovered);
    mutateGetDevices().then(setDevices);
    mutateGetGroups().then(setGroups);
  };

  useEffect(() => {
    reload();
  }, []);

  const domainsInUse = useMemo(
    () => new Set(devices.map((d) => d.haDomain)).size,
    [devices],
  );

  const handleOpenLink = (entity: IHomeAssistantDiscoveredEntity) => {
    setEntityToLink(entity);
    setLinkName(entity.friendlyName);
    setLinkGroupId(undefined);
  };

  const handleCloseLink = () => {
    setEntityToLink(undefined);
    setLinkName('');
    setLinkGroupId(undefined);
  };

  const handleConfirmLink = async () => {
    if (!entityToLink) return;
    await mutateLink({
      name: linkName,
      haEntityId: entityToLink.haEntityId,
      deviceGroupId: linkGroupId,
    });
    showSnackbar('Dispositivo vinculado com sucesso.', 'success');
    handleCloseLink();
    reload();
  };

  const handleTrigger = async (
    device: IHomeAssistantDeviceProps,
    service: 'turn_on' | 'turn_off',
  ) => {
    setTriggeringId(device.id);
    try {
      const result = await mutateTrigger({ id: device.id, data: { service } });
      showSnackbar(
        result.success
          ? `${device.name}: comando enviado com sucesso.`
          : `${device.name}: falha ao acionar (${result.error ?? 'erro desconhecido'}).`,
        result.success ? 'success' : 'error',
      );
    } finally {
      setTriggeringId(undefined);
    }
  };

  return (
    <Box>
      <Grid2 container spacing={2} sx={{ mb: 4 }}>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <StatTile
            icon={<DevicesOther fontSize="inherit" />}
            label="Dispositivos vinculados"
            value={devices.length}
            accentColor="#38bdf8"
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <StatTile
            icon={<Search fontSize="inherit" />}
            label="Descobertos pendentes"
            value={discovered.length}
            accentColor="#f59e0b"
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <StatTile
            icon={<Bolt fontSize="inherit" />}
            label="Domínios em uso"
            value={domainsInUse}
            accentColor="#a855f7"
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
          <StatTile
            icon={<Lightbulb fontSize="inherit" />}
            label="Grupos disponíveis"
            value={groups.length}
            accentColor="#22c55e"
          />
        </Grid2>
      </Grid2>

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
        Meus dispositivos
      </Typography>

      {isLoadingDevices ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Carregando dispositivos...
        </Typography>
      ) : devices.length === 0 ? (
        <Paper sx={{ ...cardSx(), textAlign: 'center', color: 'text.secondary' }}>
          Nenhum dispositivo vinculado ainda. Vincule um dispositivo descoberto abaixo.
        </Paper>
      ) : (
        <Grid2 container spacing={2}>
          {devices.map((device) => (
            <Grid2 key={device.id} size={{ xs: 12, sm: 6, lg: 4 }}>
              <Box sx={cardSx()}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: alpha('#38bdf8', 0.15),
                      color: '#38bdf8',
                      flexShrink: 0,
                    }}
                  >
                    <Lightbulb />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 700 }}>
                      {device.name}
                    </Typography>
                    <Typography noWrap variant="caption" sx={{ color: 'text.secondary' }}>
                      {device.haEntityId}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1}>
                  <ButtonComponent
                    fullWidth
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<PowerSettingsNew fontSize="small" />}
                    disabled={triggeringId === device.id}
                    onClick={() => handleTrigger(device, 'turn_on')}
                  >
                    Ligar
                  </ButtonComponent>
                  <ButtonComponent
                    fullWidth
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<PowerSettingsNew fontSize="small" />}
                    disabled={triggeringId === device.id}
                    onClick={() => handleTrigger(device, 'turn_off')}
                  >
                    Desligar
                  </ButtonComponent>
                </Stack>
              </Box>
            </Grid2>
          ))}
        </Grid2>
      )}

      <Box sx={{ mt: 5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Descobertos no Home Assistant
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Entidades já existentes no Home Assistant que ainda não foram vinculadas a um
          dispositivo do AlertAI.
        </Typography>

        {isLoadingDiscovered ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Buscando entidades no Home Assistant...
          </Typography>
        ) : discovered.length === 0 ? (
          <Paper sx={{ ...cardSx(), textAlign: 'center', color: 'text.secondary' }}>
            Nenhuma entidade nova encontrada. Configure a integração Home Assistant primeiro.
          </Paper>
        ) : (
          <Stack spacing={1.5}>
            {discovered.map((entity) => (
              <Box
                key={entity.haEntityId}
                sx={{
                  ...cardSx(),
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600 }}>{entity.friendlyName}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Chip size="small" label={entity.haEntityId} variant="outlined" />
                    <Chip
                      size="small"
                      label={entity.haDomain}
                      variant="outlined"
                      sx={{ borderColor: '#38bdf8', color: '#38bdf8' }}
                    />
                  </Stack>
                </Box>
                <ButtonComponent
                  variant="contained"
                  size="small"
                  startIcon={<LinkIcon fontSize="small" />}
                  onClick={() => handleOpenLink(entity)}
                >
                  Vincular
                </ButtonComponent>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <ModalComponent
        isOpen={Boolean(entityToLink)}
        onClose={handleCloseLink}
        title="Vincular dispositivo"
      >
        <Box sx={{ minWidth: 320 }}>
          {entityToLink && (
            <Chip size="small" label={entityToLink.haEntityId} sx={{ mb: 2 }} />
          )}
          <TextFieldComponent
            label="Nome do dispositivo"
            value={linkName}
            onChangeText={setLinkName}
            fullWidth
            sx={{ mb: 2 }}
          />
          <DropdownSearchbleComponent<IDeviceGroupSimpleProps, number>
            label="Grupo (opcional)"
            value={linkGroupId}
            labelField={'name'}
            valueField={'id'}
            options={groups}
            onChange={(newValue) => setLinkGroupId(newValue ?? undefined)}
          />
          <ButtonComponent
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            disabled={!linkName || isLoadingLink}
            onClick={handleConfirmLink}
          >
            Vincular
          </ButtonComponent>
        </Box>
      </ModalComponent>
    </Box>
  );
};

export default DevicesTab;
